// One of the employee's own Talk to HR requests: what they sent, its status in their words, and
// the conversation with HR as a thread (their replies on the right, HR's on the left). HR's notes
// and who owns the request aren't here. "/requests/<id>" opens it in place of the conversation
// (keyed by id, so each request starts fresh).

import { Fragment, useEffect, useState } from "react";
import { CATEGORIES, EMPLOYEE_STATUS_LABELS, type EmployeeRequest, type EmployeeRequestDetail, type EmployeeTimelineEntry, type EscalationStatus } from "@ask-hr/core/escalations";
import { ApiError, api } from "../api.js";
import { AttachPicker, AttachmentLinks, uploadAll } from "../attachments.js";
import { linkTo } from "../router.js";

export function requestIdOf(path: string): string | null {
  return /^\/requests\/([0-9a-z]{9,40})$/.exec(path)?.[1] ?? null;
}

// Which comments the employee has seen, kept in this browser only: enough to mark a request with
// a new comment, without the server tracking reads.
const SEEN_KEY = "novara-askhr-requests-seen";

function seenMap(): Record<string, string> {
  try {
    const v = JSON.parse(localStorage.getItem(SEEN_KEY) ?? "{}") as unknown;
    return typeof v === "object" && v !== null ? (v as Record<string, string>) : {};
  } catch {
    return {};
  }
}

export function hasUnseenComment(r: EmployeeRequest): boolean {
  return Boolean(r.lastCommentAt && r.lastCommentAt > (seenMap()[r.id] ?? ""));
}

function markSeen(r: EmployeeRequest): void {
  if (!r.lastCommentAt) return;
  try {
    localStorage.setItem(SEEN_KEY, JSON.stringify({ ...seenMap(), [r.id]: r.lastCommentAt }));
  } catch {
    // storage full or blocked: the marker just stays
  }
}

// "HR replied" stands out: the next step is theirs.
export const STATUS_PILL: Record<EscalationStatus, string> = { new: "pill", in_progress: "pill", waiting: "pill alert", resolved: "pill ok" };
const when = (iso: string) => new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" });

// What's happening, in a line under the heading.
const BANNER: Record<EscalationStatus, string> = {
  new: "HR has your request. You'll get an email and a Slack message when they reply.",
  in_progress: "Someone in HR is looking into this.",
  waiting: "HR replied. Read it below, and reply if you need to.",
  resolved: "HR marked this resolved. If you still need help, reply and it opens again.",
};

// The status lines in the thread: sent, resolved, and opened again (the API sends only these).
function statusLine(t: EmployeeTimelineEntry): string | null {
  if (t.status === "new") return "Sent to HR";
  if (t.status === "resolved") return "HR marked this resolved";
  if (t.status) return t.comment?.mine ? "Your reply opened this again" : "HR opened this again";
  return null;
}

// onReplied: their list refreshes, since a reply changes the request's status.
export function MyRequest({ id, onSeen, onReplied }: { id: string; onSeen: () => void; onReplied?: () => void }) {
  const [request, setRequest] = useState<EmployeeRequestDetail | null>(null);
  const [error, setError] = useState<"not_found" | "failed" | null>(null);
  const [reply, setReply] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [problem, setProblem] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  async function send() {
    setSending(true);
    setProblem(null);
    setSent(false);
    try {
      const base = `/api/escalations/${id}`;
      const attachments = files.length ? await uploadAll(base, files) : [];
      const r = await api<{ request: EmployeeRequestDetail }>("POST", `${base}/replies`, { note: reply, attachments });
      setRequest(r.request);
      onReplied?.();
      setReply("");
      setFiles([]);
      setSent(true);
    } catch (e) {
      setProblem(e instanceof Error && e.message ? e.message : "Your reply didn't send. Try again.");
    } finally {
      setSending(false);
    }
  }

  useEffect(() => {
    let live = true;
    api<{ request: EmployeeRequestDetail }>("GET", `/api/escalations/${id}`).then(
      (r) => {
        if (!live) return;
        setRequest(r.request);
        markSeen(r.request);
        onSeen();
      },
      (e: unknown) => live && setError(e instanceof ApiError && e.status === 404 ? "not_found" : "failed"),
    );
    return () => {
      live = false;
    };
    // onSeen only refreshes the pane's markers; a new id is what matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return (
    <div className="my-request panel">
      <p>
        <a href="/" onClick={linkTo("/")}>
          ← Back to Ask HR
        </a>
      </p>
      {error ? (
        <p className="notice" role="alert">
          {error === "not_found" ? "That request couldn't be found. It may be older than 90 days." : "Couldn't load the request. Try again."}
        </p>
      ) : !request ? (
        <p className="sub">Loading…</p>
      ) : (
        <>
          <div className="my-request-head">
            <h2>{CATEGORIES[request.category]}</h2>
            <span className={STATUS_PILL[request.status]}>{EMPLOYEE_STATUS_LABELS[request.status]}</span>
          </div>
          <p className="sub">
            {request.number ? `Request #${request.number} · sent` : "Sent"} to HR {when(request.createdAt)}
            {request.channel === "slack" ? " from Slack" : ""}.
          </p>
          <p className={`banner ${request.status}`}>{BANNER[request.status]}</p>

          {request.note ? (
            <>
              <h3>Your note</h3>
              <p className="prewrap">{request.note}</p>
            </>
          ) : null}
          {request.excerpt.length ? (
            <details className="shared">
              <summary>The conversation you shared ({request.excerpt.length} messages)</summary>
              <div className="extract">
                {request.excerpt.map((t, i) => (
                  <p key={i} className="prewrap">
                    <strong>{t.role === "user" ? "You" : "Ask HR"}:</strong> {t.text}
                  </p>
                ))}
              </div>
            </details>
          ) : null}

          <h3>Updates</h3>
          <div className="ethread">
            {request.timeline.map((t, i) => {
              const line = statusLine(t);
              return (
                <Fragment key={i}>
                  {/* A reply that reopened the request: the reply first, then the line it caused. */}
                  {t.comment ? (
                    <div className={t.comment.mine ? "rmsg mine" : "rmsg them"}>
                      <div className="who">
                        <b>{t.comment.mine ? "You" : t.comment.by}</b>
                        <span>
                          {t.comment.mine ? "" : "HR · "}
                          {when(t.at)}
                        </span>
                      </div>
                      {t.comment.text ? <p>{t.comment.text}</p> : null}
                      {t.comment.attachments?.length ? (
                        <AttachmentLinks items={t.comment.attachments} path={(a) => `/api/escalations/${request.id}/attachments/${a.id}`} />
                      ) : null}
                    </div>
                  ) : null}
                  {line ? (
                    <div className="sys">
                      {line} · {when(t.at)}
                    </div>
                  ) : null}
                </Fragment>
              );
            })}
          </div>
          <p className="sub">HR replies here. You get an email and a Slack message when they do.</p>

          <div className="field reply">
            <label htmlFor={`reply-${request.id}`}>Reply to HR</label>
            <textarea id={`reply-${request.id}`} rows={3} maxLength={2000} value={reply} disabled={sending} onChange={(e) => setReply(e.target.value)} />
            <AttachPicker files={files} onChange={setFiles} onProblem={setProblem} disabled={sending} />
            {problem ? (
              <p className="notice" role="alert">
                {problem}
              </p>
            ) : null}
            <div className="actions start">
              <button type="button" className="primary" disabled={sending || (!reply.trim() && !files.length)} onClick={() => void send()}>
                {sending ? "Sending…" : "Send reply"}
              </button>
              {sent ? (
                <span className="muted small" role="status">
                  Sent. HR will see it on your request.
                </span>
              ) : null}
            </div>
            <span className="muted small">
              Only you and HR see this.{request.status === "resolved" ? " Replying opens the request again." : ""}
            </span>
          </div>
        </>
      )}
    </div>
  );
}
